import { useMemo } from "react";
import { ScrollView, useWindowDimensions } from "react-native";
import { View, Text } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { WeekCalendar } from "./WeekCalendar";
import { ClipVideoPlayer } from "./ClipVideoPlayer";
import { ClipsTimeline } from "./ClipsTimeline";
import { ActivityHeatmap } from "./ActivityHeatmap";
import { useUnistyles } from "react-native-unistyles";
import { getHours, startOfDay } from "date-fns";
import Animated, { FadeInUp, FadeOutDown } from "react-native-reanimated";
import { useClipsLogic } from "../hooks/useClipsLogic";
import { Icon } from "@src/components/Icon";
import { useCameraPermissions } from "@visioni/a3s-access/app";

interface ClipsScreenContentProps {
  siteId: string;
  cameraKey: string;
  timezone: string;
  initialClipId?: string;
  initialTimestamp?: string;
  initialHour?: string;
}

function parseValidDate(value?: string): Date | null {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function parseHour(hourParam?: string): number | null {
  const parsed = Number(hourParam);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 23 ? parsed : null;
}

export function ClipsScreenContent({
  siteId,
  cameraKey,
  timezone,
  initialClipId,
  initialTimestamp,
  initialHour,
}: ClipsScreenContentProps) {
  const { theme } = useUnistyles();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const { canViewClips, isLoading: permissionsLoading } = useCameraPermissions(siteId, cameraKey);

  const initialDateTime = parseValidDate(initialTimestamp);
  const deepLinkHour = parseHour(initialHour);

  const initialSelection = useMemo(
    () => ({
      date: initialDateTime ? startOfDay(initialDateTime) : undefined,
      hour: deepLinkHour ?? (initialDateTime ? getHours(initialDateTime) : undefined),
      clipId: initialClipId,
    }),
    // Only compute once on mount — deep-link params don't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    selectedDate,
    selectedHour,
    today,
    dateDirection,
    handleDateSelect,
    handleHourSelect,

    hourBuckets,
    isLoadingHourlyClipCounts,

    clipsData,
    isLoadingClips,
    isFetchingClips,
    clipsError,
    refetchAll,

    selectedClip,
    selectedClipId,
    selectedClipIndex,
    totalClipsInHour,
    handleClipSelect,
    handlePrevious,
    handleNext,
    hasPrevious,
    hasNext,
    handleClipViewed,

    hasMore,
    handleLoadMore,
  } = useClipsLogic({ siteId, cameraKey, timezone, initialSelection, enabled: !permissionsLoading && canViewClips });

  const videoPlayerProps = useMemo(
    () => ({
      clip: selectedClip,
      isLoading: isLoadingClips,
      onPrevious: handlePrevious,
      onNext: handleNext,
      hasPrevious,
      hasNext,
      onViewed: handleClipViewed,
      screenHeight,
      screenWidth,
      selectedHour,
    }),
    [
      selectedClip,
      handlePrevious,
      handleNext,
      hasPrevious,
      hasNext,
      handleClipViewed,
      screenHeight,
      screenWidth,
      selectedHour,
      isLoadingClips,
    ],
  );

  if (permissionsLoading) {
    return (
      <View flex={1} bg="background" style={{ justifyContent: "center", alignItems: "center" }}>
        <Text textStyle="md/regular" style={{ color: theme.colors.grey[500] }}>
          Loading...
        </Text>
      </View>
    );
  }

  if (!canViewClips) {
    return (
      <View flex={1} bg="background" style={{ justifyContent: "center", alignItems: "center" }}>
        <Icon name="lockClosed" size={64} color={theme.colors.grey[400]} style={{ marginBottom: theme.spacing.xl }} />
        <Text textStyle="lg/bold" style={{ color: theme.colors.text, textAlign: "center" }} mb="md">
          Restricted Access
        </Text>
        <Text textStyle="md/regular" style={{ color: theme.colors.grey[500], textAlign: "center", maxWidth: 320 }}>
          You don&apos;t have permission to view camera recordings. Please contact your administrator.
        </Text>
      </View>
    );
  }

  return (
    <View flex={1} bg="background">
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View px="2xl" mb="4xl">
          <View px="3xl" py="md" bg="surfaceCard" rounded={theme.spacing.xl} borderWidth={1} borderColor="borderCard">
            <WeekCalendar selectedDate={selectedDate} onDateSelect={handleDateSelect} today={today} />
            <View height={1} bg="borderCard" my="xs" />
            <ActivityHeatmap
              selectedDate={selectedDate}
              selectedHour={selectedHour}
              onHourSelect={handleHourSelect}
              hourBuckets={hourBuckets}
              isLoading={isLoadingHourlyClipCounts}
              dateDirection={dateDirection}
              timezone={timezone}
            />
          </View>
        </View>
        <DataStateView
          data={clipsData}
          isLoading={isLoadingClips}
          isFetching={isFetchingClips}
          error={clipsError}
          refetch={refetchAll}
          isEmpty={(clips) => clips.length === 0}
          loadingMessage="Loading clips..."
          errorTitle="Failed to load clips"
          errorMessage="Please check your connection and try again."
          emptyState={{
            icon: "videoOutline",
            title: "No clips found",
            message: "There are no clips available for this hour.",
          }}
        >
          {(clips) => (
            <Animated.View
              entering={FadeInUp}
              exiting={FadeOutDown}
              key={selectedDate.toISOString() + selectedHour.toString()}
            >
              <View justifyContent="center" px="lg" mb="lg">
                <ClipVideoPlayer
                  {...videoPlayerProps}
                  clipIndex={selectedClipIndex + 1}
                  totalClips={totalClipsInHour}
                />
              </View>
              <ClipsTimeline
                clips={clips}
                selectedClipId={selectedClipId}
                onClipSelect={handleClipSelect}
                timezone={timezone}
                hasNextPage={hasMore}
                isFetchingNextPage={false}
                onLoadMore={handleLoadMore}
              />
            </Animated.View>
          )}
        </DataStateView>
      </ScrollView>
    </View>
  );
}
